import logger from '../log';

const log = logger.getLogger(__filename);

// do not need coverage on code copied from google
/* istanbul ignore file */

// Copied from https://shaka-player-demo.appspot.com/docs/api/lib_net_http_plugin_utils.js.html
const makeResponse = function(
  headers,
  data,
  status,
  uri,
  responseURL,
  requestType
) {
  if (status >= 200 && status <= 299 && status != 202) {
    // Most 2xx HTTP codes are success cases.
    /** @type {shaka.extern.Response} */
    const response = {
      uri: responseURL || uri,
      originalUri: uri,
      data: data,
      headers: headers,
      fromCache: !!headers['x-shaka-from-cache']
    };
    return response;
  } else {
    let responseText = null;
    const shaka = window.shaka;
    try {
      responseText = shaka.util.StringUtils.fromBytesAutoDetect(data);
    } catch (exception) {
      // No body
    }
    log.debug('HTTP error text:', responseText);
    const severity =
      status == 401 || status == 403
        ? shaka.util.Error.Severity.CRITICAL
        : shaka.util.Error.Severity.RECOVERABLE;
    throw new shaka.util.Error(
      severity,
      shaka.util.Error.Category.NETWORK,
      shaka.util.Error.Code.BAD_HTTP_STATUS,
      uri,
      status,
      responseText,
      headers,
      requestType
    );
  }
};

export default function buildshakaplugin(xhrFactory) {
  // this code is from shaka with minor modifications - removed calls to goog.asserts and commented out unnused vars.
  // pulled from https://shaka-player-demo.appspot.com/docs/api/lib_net_http_xhr_plugin.js.html
  return function(uri, request, requestType, progressUpdated) {
    const shaka = window.shaka;
    const xhr = xhrFactory();
    log.trace(
      'shakaXhrPlugin entry',
      uri,
      request,
      requestType,
      progressUpdated
    );
    // last time stamp when we got a progress event.
    let lastTime = Date.now();
    // last number of bytes loaded, from progress event.
    let lastLoaded = 0;
    const promise = new Promise(function(resolve, reject) {
      xhr.open(request.method, uri, true);
      xhr.responseType = 'arraybuffer';
      xhr.timeout = request.retryParameters.timeout;
      xhr.withCredentials = request.allowCrossSiteCredentials;
      xhr.onabort = function() {
        reject(
          new shaka.util.Error(
            shaka.util.Error.Severity.RECOVERABLE,
            shaka.util.Error.Category.NETWORK,
            shaka.util.Error.Code.OPERATION_ABORTED,
            uri,
            requestType
          )
        );
      };
      xhr.onload = function(event) {
        const target = event.target;
        // Since IE and Edge incorrectly return the header with a leading new line
        // character ('\n'), we trim the header here.
        const headerLines = target
          .getAllResponseHeaders()
          .trim()
          .split('\r\n');
        const headers = {};
        for (const header of headerLines) {
          /** @type {!Array.<string>} */
          const parts = header.split(': ');
          headers[parts[0].toLowerCase()] = parts.slice(1).join(': ');
        }
        try {
          const response = makeResponse(
            headers,
            target.response,
            target.status,
            uri,
            target.responseURL,
            requestType
          );
          resolve(response);
        } catch (error) {
          reject(error);
        }
      };
      xhr.onerror = function(event) {
        reject(
          new shaka.util.Error(
            shaka.util.Error.Severity.RECOVERABLE,
            shaka.util.Error.Category.NETWORK,
            shaka.util.Error.Code.HTTP_ERROR,
            uri,
            event,
            requestType
          )
        );
      };
      xhr.ontimeout = function(/*event*/) {
        reject(
          new shaka.util.Error(
            shaka.util.Error.Severity.RECOVERABLE,
            shaka.util.Error.Category.NETWORK,
            shaka.util.Error.Code.TIMEOUT,
            uri,
            requestType
          )
        );
      };
      xhr.onprogress = function(event) {
        const currentTime = Date.now();
        // If the time between last time and this time we got progress event
        // is long enough, or if a whole segment is downloaded, call
        // progressUpdated().
        if (
          currentTime - lastTime > 100 ||
          (event.lengthComputable && event.loaded == event.total)
        ) {
          try {
            progressUpdated(
              currentTime - lastTime,
              event.loaded - lastLoaded,
              event.total - event.loaded
            );
          } catch (e) {
            log.warn(
              'Failed calling progressUpdated in shaka xhr plugin',
              e,
              progressUpdated,
              request
            );
          }
          lastLoaded = event.loaded;
          lastTime = currentTime;
        }
      };
      for (const key in request.headers) {
        // The Fetch API automatically normalizes outgoing header keys to
        // lowercase. For consistency's sake, do it here too.
        const lowercasedKey = key.toLowerCase();
        xhr.setRequestHeader(lowercasedKey, request.headers[key]);
      }
      xhr.send(request.body);
    });
    return new shaka.util.AbortableOperation(promise, () => {
      xhr.abort();
      return Promise.resolve();
    });
  };
}
